import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "../../../utils/axios";
import { toast } from "react-toastify";
import LoadingComponent from "../../../components/LoadingComponent";
import Button from "../../../components/Button";
import NoData from "../../../components/NoData";
import { ExclamationTriangleIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import React from "react";

export default function DeliveryNoteModal({ invoiceNumber, onClose, onSubmitSuccess }) {
    const queryClient = useQueryClient();

    const [isSending, setIsSending] = useState(false);

    const [abaForm, setAbaForm] = useState({
        used_photocopiers:
            "Used and fully functional multifunctional photocopiers incl. options",
        photocopiers_sets: "",
        finishers_count: "",
        collis_count: "",
        weight: "",
    });

    const inputClass =
        "w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm";

    const handleSubmitClose = () => {
        if (onSubmitSuccess) {
            onSubmitSuccess();
        } else {
            onClose();
        }
    };

    // ── 1. Check if an AbaNinja invoice exists for this invoice number ──────────
    const { data: abaNinjaInvoice, isLoading: checkingAbaNinja } = useQuery({
        queryKey: ["abaninja-invoice-check", invoiceNumber],
        queryFn: async () => {
            if (!invoiceNumber) return null;
            try {
                const response = await axiosInstance.get(
                    `${window.REACT_APP_API_ENDPOINT}/abaninja/invoices/by-invoice-number/${invoiceNumber}`
                );
                return response.data;
            } catch (error) {
                if (error.response?.status === 404) return null;
                throw error;
            }
        },
        enabled: !!invoiceNumber,
        staleTime: 5 * 60 * 1000,
    });

    const abaNinjaInvoiceId = abaNinjaInvoice?.data?.id ?? null;

    // ── 2. Fetch all devices for this invoice ────────────────────────────────────
    const { data: abaDevices, isLoading: devicesLoading } = useQuery({
        queryKey: ["delivery-note-devices", invoiceNumber],
        queryFn: async () => {
            const response = await axiosInstance.get(
                `${window.REACT_APP_API_ENDPOINT}/device-statuses/by-invoice-number/${invoiceNumber}?type=all`
            );
            return response.data;
        },
        enabled: !!abaNinjaInvoiceId,
    });

    // ── 3. Fetch existing delivery note from our DB ───────────────────────────────
    const { data: deliveryNote, isLoading: checkingDeliveryNote } = useQuery({
        queryKey: ["delivery-note", invoiceNumber],
        queryFn: async () => {
            try {
                const response = await axiosInstance.get(
                    `${window.REACT_APP_API_ENDPOINT}/abaninja/delivery-notes/by-invoice/${invoiceNumber}`
                );
                return response.data;
            } catch (error) {
                if (error.response?.status === 404) return null;
                throw error;
            }
        },
        enabled: !!abaNinjaInvoice?.data,
        staleTime: 5 * 60 * 1000,
    });

    // ── 4. Bulk-update price for all devices sharing the same brand+model ────────
    const bulkUpdatePriceMutation = useMutation({
        mutationFn: ({ brand, model, price }) =>
            axiosInstance.patch(
                `${window.REACT_APP_API_ENDPOINT}/devices/bulk-price`,
                { invoice_number: invoiceNumber, brand, model, price }
            ),
        onSuccess: () => {
            queryClient.invalidateQueries(["delivery-note-devices", invoiceNumber]);
        },
        onError: () => {
            toast.error("Failed to bulk-update device prices.");
        },
    });

    // ── 5. Update abaninja_invoices record on our backend ────────────────────────
    const updateAbaNinjaInvoiceMutation = useMutation({
        mutationFn: ({ id, data }) =>
            axiosInstance.patch(
                `${window.REACT_APP_API_ENDPOINT}/abaninja/invoices/${id}`,
                data
            ),
        onSuccess: () => {
            queryClient.invalidateQueries(["abaninja-invoice-check"]);
        },
        onError: (error) => {
            toast.error(
                error.response?.data?.error || "Failed to update AbaNinja invoice record."
            );
        },
    });

    // ── 6. Save delivery note record on our backend ──────────────────────────────
    const saveDeliveryNoteToBackend = useMutation({
        mutationFn: ({ abaninja_uuid }) =>
            axiosInstance.post(
                `${window.REACT_APP_API_ENDPOINT}/abaninja/delivery-notes`,
                { abaninja_invoice_id: invoiceNumber, abaninja_uuid }
            ),
        onSuccess: () => {
            queryClient.invalidateQueries(["delivery-note", invoiceNumber]);
        },
        onError: (error) => {
            toast.error(
                error.response?.data?.error || "Failed to save delivery note record."
            );
        },
    });

    // ── 7. Group devices by brand+model — one row per group in the table ─────────
    const groupedDevices = React.useMemo(() => {
        if (!abaDevices?.data?.data) return [];
        const map = {};
        abaDevices.data.data.forEach((device) => {
            const key = `${device.brand}||${device.model}`;
            if (!map[key]) {
                map[key] = {
                    key,
                    brand: device.brand,
                    model: device.model,
                    count: 0,
                    deviceIds: [],
                    prices: [],
                };
            }
            map[key].count += 1;
            map[key].deviceIds.push(device.id);
            map[key].prices.push(device.price);
        });
        return Object.values(map);
    }, [abaDevices]);

    // ── 8. Auto-sync: fix groups where some devices have a price and others don't ─
    useEffect(() => {
        if (!groupedDevices.length) return;
        groupedDevices.forEach((group) => {
            const nonNullPrices = group.prices.filter(
                (p) => p !== null && p !== "" && p !== undefined
            );
            const hasNull = group.prices.some(
                (p) => p === null || p === "" || p === undefined
            );
            if (nonNullPrices.length > 0 && hasNull) {
                bulkUpdatePriceMutation.mutate({
                    brand: group.brand,
                    model: group.model,
                    price: nonNullPrices[0],
                });
            }
        });
    }, [groupedDevices]);

    // ── 9. Auto-populate form fields from devices ────────────────────────────────
    useEffect(() => {
        if (!abaDevices?.data?.data) return;
        const devices = abaDevices.data.data;
        const numberOfDevices = abaDevices.data.total ?? devices.length;

        const totalFinishers = devices.reduce((sum, device) => {
            const opts = device.device_options || {};
            const mainFinisher = opts.finisher === 1 || opts.finisher === true ? 1 : 0;
            const internalFinisher = Number(opts.internal_finisher) || 0;
            return sum + mainFinisher + internalFinisher;
        }, 0);

        const devicesWeight = devices.reduce(
            (sum, device) => sum + Number(device.model_weight || 0),
            0
        );
        const finalWeight = Math.round(devicesWeight + totalFinishers * 40);

        setAbaForm((prev) => ({
            ...prev,
            photocopiers_sets: String(numberOfDevices),
            finishers_count: String(totalFinishers),
            collis_count: String(numberOfDevices + totalFinishers),
            weight: String(finalWeight),
        }));
    }, [abaDevices]);

    // ── 10. Pre-fill form from existing abaninja_invoices record ─────────────────
    useEffect(() => {
        if (!abaNinjaInvoice?.data) return;
        const inv = abaNinjaInvoice.data;
        setAbaForm((prev) => ({
            ...prev,
            ...(inv.used_photocopiers ? { used_photocopiers: inv.used_photocopiers } : {}),
            ...(inv.photocopiers_sets ? { photocopiers_sets: String(inv.photocopiers_sets) } : {}),
            ...(inv.finishers_count != null ? { finishers_count: String(inv.finishers_count) } : {}),
            ...(inv.collis_count != null ? { collis_count: String(inv.collis_count) } : {}),
            ...(inv.weight ? { weight: String(inv.weight) } : {}),
        }));
    }, [abaNinjaInvoice]);

    const devices = abaDevices?.data?.data ?? [];

    const isLoading =
        checkingAbaNinja ||
        (!!abaNinjaInvoice?.data && (checkingDeliveryNote || devicesLoading));

    // ── Core: build payload and create/update delivery note via AbaNinja API ──────
    const handleSubmit = async () => {
        if (!devices.length) {
            toast.error("No devices found for this invoice.");
            return;
        }

        setIsSending(true);
        try {
            const ABA_ACCOUNT_UUID = window.REACT_APP_ABANINJA_ACCOUNT_UUID;
            const ABA_API_KEY = window.REACT_APP_ABANINJA_API_KEY;
            const abaHeaders = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${ABA_API_KEY}`,
            };

            // ── 1. Fetch AbaNinja persons ──
            const abaPersonsRes = await fetch(
                `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/addresses/v2/persons?limit=500`,
                { headers: abaHeaders }
            );
            if (!abaPersonsRes.ok) throw new Error("Failed to fetch AbaNinja persons.");
            const abaPersonsData = await abaPersonsRes.json();
            const abaPersons = abaPersonsData?.data ?? [];

            // ── 2. Match receiver — try persons first, then fall back to companies ──
            const invoiceClientUniqueId =
                devices[0]?.client_unique_id ?? devices[0]?.client?.unique_id;

            let matchedAbaAddress = abaPersons.find(
                (p) => String(p.customer_number) === String(invoiceClientUniqueId)
            );
            let isCompanyAddress = false;

            if (!matchedAbaAddress) {
                const abaCompaniesRes = await fetch(
                    `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/addresses/v2/companies?limit=500`,
                    { headers: abaHeaders }
                );
                if (!abaCompaniesRes.ok) throw new Error("Failed to fetch AbaNinja companies.");
                const abaCompaniesData = await abaCompaniesRes.json();
                const abaCompanies = abaCompaniesData?.data ?? [];

                matchedAbaAddress = abaCompanies.find(
                    (c) => String(c.customer_number) === String(invoiceClientUniqueId)
                );
                if (matchedAbaAddress) isCompanyAddress = true;
            }

            if (!matchedAbaAddress) {
                toast.error(
                    `Could not find AbaNinja person or company matching client ID: ${invoiceClientUniqueId}`
                );
                return;
            }

            // ── 3. Determine CH vs EX ──
            const firstDevice = devices[0];
            const marketType =
                abaDevices?.client_market_type ?? firstDevice?.client_market_type;
            const isChCustomer = marketType === "CH";

            const productCode = isChCustomer
                ? firstDevice?.swiss_product_code ?? ""
                : firstDevice?.international_product_code ?? "";

            // ── 4. Today's date ──
            const today = new Date();
            const deliveryNoteDateStr = today.toISOString().split("T")[0];

            // ── 5. Build positions (group by brand+model, no price) ──
            const groupMap = {};
            devices.forEach((device) => {
                const key = `${device.brand}||${device.model}`;
                if (!groupMap[key]) {
                    groupMap[key] = {
                        brand: device.brand,
                        model: device.model,
                        serials: [],
                        exportCode: productCode,
                    };
                }
                groupMap[key].serials.push(device.serial_no);
            });

            const positions = [];
            let posNum = 1;
            Object.values(groupMap).forEach((group) => {
                // Since all devices in a group share the same brand+model,
                // one description line is enough.
                const serialsText = group.serials.length > 0 
                    ? `\nS/N: ${group.serials.join(", ")}` 
                    : "";

                const description = isChCustomer
                    ? `${group.model}, ${marketType}, Freight${serialsText}`
                    : `${group.model}, ${group.brand}${serialsText}`;

                positions.push({
                    kind: "product",
                    positionNumber: posNum++,
                    productNumber: group.model,
                    productDescription: description,
                    quantity: group.serials.length,
                    discount: { amount: 0 },
                    vat: { percentage: 0, amount: 0 },
                });
            });

            // ── 6. Build delivery note payload ──
            const deliveryNotePayload = {
                documents: [
                    {
                        isTemplate: false,
                        receiver: {
                            addressUuid: matchedAbaAddress.addresses?.[0]?.uuid ?? undefined,
                            ...(isCompanyAddress
                                ? { companyUuid: matchedAbaAddress.uuid }
                                : { personUuid: matchedAbaAddress.uuid }),
                        },
                        currencyCode: matchedAbaAddress.currency_code || "CHF",
                        title: `Delivery Note ${invoiceNumber}`,
                        reference: "",
                        deliveryNoteDate: deliveryNoteDateStr,
                        deliveryDate: deliveryNoteDateStr,
                        headerText: abaForm.used_photocopiers,
                        publicNotes: [
                            `Photocopiers Sets: ${abaForm.photocopiers_sets}`,
                            `Finishers Count: ${abaForm.finishers_count}`,
                            `Collis Count: ${abaForm.collis_count}`,
                            `Weight: ${abaForm.weight}kg`,
                        ].join("\n"),
                        footerText: "",
                        positions,
                    },
                ],
            };

            // ── 7. Check if delivery note already exists ──
            const existingAbaUuid = deliveryNote?.data?.abaninja_uuid;

            // Shared: sync abaForm fields back to the abaninja_invoices record
            const syncFormToInvoiceRecord = () => {
                if (!abaNinjaInvoice?.data?.id) return;
                updateAbaNinjaInvoiceMutation.mutate({
                    id: abaNinjaInvoice.data.id,
                    data: {
                        used_photocopiers: abaForm.used_photocopiers,
                        photocopiers_sets: abaForm.photocopiers_sets,
                        finishers_count: abaForm.finishers_count,
                        collis_count: abaForm.collis_count,
                        weight: abaForm.weight,
                    },
                });
            };

            if (existingAbaUuid) {
                // UPDATE existing delivery note in AbaNinja
                const patchRes = await fetch(
                    `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/documents/v2/delivery-notes/${existingAbaUuid}`,
                    {
                        method: "PATCH",
                        headers: abaHeaders,
                        body: JSON.stringify(deliveryNotePayload),
                    }
                );
                const patchResult = await patchRes.json();
                if (!patchRes.ok) {
                    console.error("AbaNinja PATCH error:", patchResult);
                    throw new Error(
                        patchResult?.message ||
                        patchResult?.meta?.errors?.[0]?.error ||
                        "AbaNinja delivery note update failed."
                    );
                }
                console.log("AbaNinja delivery note updated — uuid:", existingAbaUuid);
                syncFormToInvoiceRecord();
                toast.success("Delivery note updated in AbaNinja successfully!");
                handleSubmitClose();
            } else {
                // CREATE new delivery note in AbaNinja
                const postRes = await fetch(
                    `https://api.abaninja.ch/accounts/${ABA_ACCOUNT_UUID}/documents/v2/delivery-notes`,
                    {
                        method: "POST",
                        headers: abaHeaders,
                        body: JSON.stringify(deliveryNotePayload),
                    }
                );
                const postResult = await postRes.json();
                if (!postRes.ok) {
                    console.error("AbaNinja POST error:", postResult);
                    throw new Error(
                        postResult?.message ||
                        postResult?.meta?.errors?.[0]?.error ||
                        "AbaNinja delivery note creation failed."
                    );
                }

                const newAbaDocument = postResult?.data?.documents?.[0] ?? null;
                const newAbaUuid = newAbaDocument?.uuid ?? postResult?.data?.uuid ?? null;
                console.log("AbaNinja delivery note created — uuid:", newAbaUuid);

                await saveDeliveryNoteToBackend.mutateAsync({ abaninja_uuid: newAbaUuid });
                syncFormToInvoiceRecord();
                toast.success("Delivery note created successfully!");
                handleSubmitClose();
            }
        } catch (err) {
            console.error("Delivery note error:", err);
            toast.error(err.message || "Failed to process delivery note.");
        } finally {
            setIsSending(false);
        }
    };

    // ── Loading ──────────────────────────────────────────────────────────────────
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-10">
                <LoadingComponent />
                <span className="ml-2 text-gray-600">Checking invoice data...</span>
            </div>
        );
    }

    // ── No AbaNinja invoice — block and inform ───────────────────────────────────
    if (!abaNinjaInvoice?.data) {
        return (
            <div className="flex flex-col items-center gap-4 py-8 px-4 text-center">
                <ExclamationTriangleIcon className="h-12 w-12 text-yellow-400" />
                <div>
                    <p className="text-gray-800 font-semibold text-base">
                        No AbaNinja Invoice Found
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                        There is no AbaNinja invoice linked to invoice{" "}
                        <span className="font-medium text-gray-700">{invoiceNumber}</span>.
                        <br />
                        Please create an AbaNinja invoice first using the{" "}
                        <span className="font-medium">"Send To AbaNinja"</span> button before
                        generating a delivery note.
                    </p>
                </div>
                <Button variant="light" size="sm" onClick={onClose}>
                    Close
                </Button>
            </div>
        );
    }

    // ── Shared form fields (same as AbaNinja modal) ───────────────────────────────
    const formFields = [
        {
            key: "used_photocopiers",
            label: "Used and fully functional multifunctional photocopiers incl. options",
        },
        { key: "photocopiers_sets", label: "Photocopiers sets (number of devices)" },
        { key: "finishers_count", label: "Finishers/Options (number of Finishers)" },
        { key: "collis_count", label: "Collis (number of Devices + number of Finishers)" },
        { key: "weight", label: "Weight (total weight, + finisher 1 ≈ 40kg)" },
    ];

    // ── Shared: invoice + client info header ─────────────────────────────────────
    const InvoiceHeader = () => (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 text-sm flex flex-wrap gap-x-8 gap-y-2">
            <div>
                <span className="text-gray-500 text-xs uppercase tracking-wide">
                    Invoice Number
                </span>
                <p className="font-semibold text-gray-800 mt-0.5">
                    {abaDevices?.invoice_number ?? invoiceNumber}
                </p>
            </div>
            <div>
                <span className="text-gray-500 text-xs uppercase tracking-wide">Client</span>
                <p className="font-semibold text-gray-800 mt-0.5">
                    {abaDevices?.client_name ?? "—"}
                    {abaDevices?.client_id && (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                            ({abaDevices.client_id})
                        </span>
                    )}
                </p>
            </div>
        </div>
    );

    // ── Shared: grouped device table (no price column) ────────────────────────────
    const DeviceTable = () => (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        {["Brand", "Model", "Total", "Weight (kg)"].map((col) => (
                            <th
                                key={col}
                                className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {col}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {groupedDevices.length > 0 ? (
                        <>
                            {groupedDevices.map((group, i) => {
                                const groupWeight = devices
                                    .filter(
                                        (d) => d.brand === group.brand && d.model === group.model
                                    )
                                    .reduce((sum, d) => sum + Number(d.model_weight || 0), 0);
                                return (
                                    <tr
                                        key={group.key}
                                        className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}
                                    >
                                        <td className="px-4 py-2 text-sm text-gray-900">
                                            {group.brand || "—"}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-900">
                                            {group.model || "—"}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-900 font-medium">
                                            {group.count}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-900">
                                            {groupWeight > 0 ? `${groupWeight} kg` : "—"}
                                        </td>
                                    </tr>
                                );
                            })}
                            <tr className="bg-gray-100 font-semibold">
                                <td
                                    colSpan={2}
                                    className="px-4 py-2 text-sm text-gray-700 text-right"
                                >
                                    Total devices:
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900">
                                    {abaDevices?.data?.total ?? devices.length}
                                </td>
                                <td />
                            </tr>
                        </>
                    ) : (
                        <tr>
                            <td colSpan={4} className="text-center py-4">
                                <NoData message="No devices found for this invoice" />
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );

    // ── Shared: form fields block ────────────────────────────────────────────────
    const FormFields = () => (
        <div className="grid grid-cols-1 gap-3">
            {formFields.map(({ key, label }, index, array) => (
                <div key={key}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        {label}
                    </label>
                    <input
                        id={`delivery-note-input-${index}`}
                        type="text"
                        value={abaForm[key]}
                        onChange={(e) =>
                            setAbaForm((p) => ({ ...p, [key]: e.target.value }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                if (index < array.length - 1) {
                                    document
                                        .getElementById(`delivery-note-input-${index + 1}`)
                                        ?.focus();
                                } else {
                                    document
                                        .getElementById("delivery-note-submit-btn")
                                        ?.focus();
                                }
                            }
                        }}
                        className={inputClass}
                    />
                </div>
            ))}
        </div>
    );

    // ── Delivery note already exists — show info + allow update ──────────────────
    if (deliveryNote?.data) {
        return (
            <div className="space-y-4">
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                    <div className="flex">
                        <div className="ml-3">
                            <p className="text-sm text-yellow-700">
                                <strong>Note:</strong> A delivery note for invoice{" "}
                                <span className="font-medium">{invoiceNumber}</span> already
                                exists in AbaNinja (UUID:{" "}
                                <span className="font-mono text-xs">
                                    {deliveryNote.data.abaninja_uuid ?? "—"}
                                </span>
                                ). Clicking "Update Delivery Note" will update it both in
                                AbaNinja and in the local record.
                            </p>
                        </div>
                    </div>
                </div>

                <InvoiceHeader />
                <DeviceTable />
                <FormFields />

                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="light" size="sm" onClick={onClose}>
                        Close
                    </Button>
                    <Button
                        id="delivery-note-submit-btn"
                        variant="warning"
                        size="sm"
                        onClick={handleSubmit}
                        disabled={isSending || devices.length === 0}
                    >
                        {isSending ? "Updating..." : "Update Delivery Note"}
                    </Button>
                </div>
            </div>
        );
    }

    // ── AbaNinja invoice exists, no delivery note yet — create ────────────────────
    return (
        <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
                <p>
                    An AbaNinja invoice was found for{" "}
                    <span className="font-medium">{invoiceNumber}</span>. No delivery note
                    has been created yet.
                </p>
                <p className="mt-1 text-blue-600">
                    AbaNinja Invoice ID:{" "}
                    <span className="font-mono font-medium">
                        {abaNinjaInvoice.data.abaninja_invoice_id}
                    </span>
                </p>
            </div>

            <InvoiceHeader />
            <DeviceTable />
            <FormFields />

            <div className="flex justify-end gap-2 pt-2">
                <Button variant="light" size="sm" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    id="delivery-note-submit-btn"
                    variant="primary"
                    size="sm"
                    onClick={handleSubmit}
                    disabled={isSending || devices.length === 0}
                >
                    {isSending ? "Creating..." : "Create Delivery Note"}
                </Button>
            </div>
        </div>
    );
}